import { AfterViewInit, Component, ElementRef, EventEmitter, forwardRef, Input, OnInit, Output, QueryList, ViewChildren } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

export const CODIGO_ACESSO_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => CodigoAcessoComponent),
  multi: true
};

/**
 * Campo de código de acesso em caixas separadas, uma por caractere.
 *
 * O código do MFA vem de GerarCodigoMfa no backend: seis caracteres de A-Z e 0-9, sempre
 * maiúsculos. Por isso o campo normaliza o que o usuário digita em vez de recusar — quem digita
 * minúsculo está certo, só escreveu de outro jeito.
 *
 * Colar TEM que funcionar. O código chega por e-mail ou SMS, e a WCAG 2.2 (Accessible
 * Authentication) trata obrigar a transcrição manual como barreira de acessibilidade. Nada aqui
 * bloqueia paste nem gerenciador de senha.
 */
@Component({
  selector: 'app-codigo-acesso',
  templateUrl: './codigo-acesso.component.html',
  styleUrls: ['./codigo-acesso.component.css'],
  providers: [CODIGO_ACESSO_VALUE_ACCESSOR]
})
export class CodigoAcessoComponent implements ControlValueAccessor, OnInit, AfterViewInit {
  /** Quantidade de caracteres do código. */
  @Input() tamanho = 6;

  /** Nome do grupo para leitor de tela. */
  @Input() rotulo = 'Código de acesso';

  /** Pinta as caixas de erro e marca aria-invalid. */
  @Input() invalido = false;

  @Input() disabled = false;

  /** Dispara quando a última caixa é preenchida, com o código inteiro. */
  @Output() completo = new EventEmitter<string>();

  @ViewChildren('caixa') caixas: QueryList<ElementRef<HTMLInputElement>>;

  indices: number[] = [];
  caracteres: string[] = [];

  private aoMudarValor: (valor: string) => void = () => { /* do nothing. */ };
  private aoTocarCampo: () => void = () => { /* do nothing. */ };

  /** Só o que o backend gera. Resto é ruído de teclado, autocorreção ou texto colado em volta. */
  private static readonly PERMITIDOS = /[^A-Z0-9]/g;

  ngOnInit(): void {
    this.indices = Array.from({ length: this.tamanho }, (_, indice) => indice);
    this.caracteres = new Array(this.tamanho).fill('');
  }

  ngAfterViewInit(): void {
    this.sincronizarCaixas();
  }

  // #region ControlValueAccessor

  writeValue(valor: string): void {
    const normalizado = this.normalizar(valor).slice(0, this.tamanho);
    this.caracteres = new Array(this.tamanho).fill('');

    for (let i = 0; i < normalizado.length; i++) {
      this.caracteres[i] = normalizado[i];
    }

    this.sincronizarCaixas();
  }

  registerOnChange(fn: (valor: string) => void): void {
    this.aoMudarValor = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.aoTocarCampo = fn;
  }

  setDisabledState(disabled: boolean): void {
    this.disabled = disabled;
  }

  // #endregion

  /** Coloca o cursor na primeira caixa vazia. Usado ao abrir a tela e ao reenviar o código. */
  focar(): void {
    const alvo = this.caracteres.findIndex(caractere => !caractere);
    this.focarCaixa(alvo < 0 ? this.tamanho - 1 : alvo);
  }

  /** Apaga o código digitado. Usado quando o backend recusa e o usuário vai tentar de novo. */
  limpar(): void {
    this.writeValue('');
    this.aoMudarValor('');
    this.focar();
  }

  aoDigitar(evento: Event, indice: number): void {
    const campo = evento.target as HTMLInputElement;

    // Teclado de celular e autocompletar entregam mais de um caractere de uma vez. O último é o
    // que a pessoa acabou de digitar; sobrescrever com ele é o que parece natural.
    const digitado = this.normalizar(campo.value);

    if (!digitado) {
      this.caracteres[indice] = '';
      this.sincronizarCaixas();
      this.publicar();
      return;
    }

    if (digitado.length > 1) {
      this.preencherDoIndice(digitado, indice);
      return;
    }

    this.caracteres[indice] = digitado;
    this.sincronizarCaixas();
    this.publicar();

    if (indice < this.tamanho - 1) {
      this.focarCaixa(indice + 1);
    }
  }

  aoTeclar(evento: KeyboardEvent, indice: number): void {
    switch (evento.key) {
      case 'Backspace':
        // Caixa vazia: apaga a anterior e volta. É o que o usuário espera de um campo segmentado.
        if (!this.caracteres[indice] && indice > 0) {
          evento.preventDefault();
          this.caracteres[indice - 1] = '';
          this.sincronizarCaixas();
          this.publicar();
          this.focarCaixa(indice - 1);
        }
        break;

      case 'Delete':
        evento.preventDefault();
        this.caracteres[indice] = '';
        this.sincronizarCaixas();
        this.publicar();
        break;

      case 'ArrowLeft':
        evento.preventDefault();
        this.focarCaixa(indice - 1);
        break;

      case 'ArrowRight':
        evento.preventDefault();
        this.focarCaixa(indice + 1);
        break;

      case 'Home':
        evento.preventDefault();
        this.focarCaixa(0);
        break;

      case 'End':
        evento.preventDefault();
        this.focarCaixa(this.tamanho - 1);
        break;

      default:
        break;
    }
  }

  aoColar(evento: ClipboardEvent, indice: number): void {
    const texto = evento.clipboardData?.getData('text') ?? '';
    const codigo = this.normalizar(texto);

    if (!codigo) {
      return;
    }

    // preventDefault só depois de saber que há conteúdo aproveitável: se o usuário colou algo que
    // não tem caractere válido, deixar o navegador seguir mostra o problema em vez de engolir.
    evento.preventDefault();
    this.preencherDoIndice(codigo, codigo.length >= this.tamanho ? 0 : indice);
  }

  aoFocar(evento: Event): void {
    // Seleciona o conteúdo para que digitar substitua em vez de ser ignorado pelo maxlength.
    (evento.target as HTMLInputElement).select();
  }

  aoSair(): void {
    this.aoTocarCampo();
  }

  porIndice(indice: number): number {
    return indice;
  }

  private preencherDoIndice(texto: string, indice: number): void {
    for (let i = 0; i < texto.length && indice + i < this.tamanho; i++) {
      this.caracteres[indice + i] = texto[i];
    }

    this.sincronizarCaixas();
    this.publicar();

    const ultimoPreenchido = Math.min(indice + texto.length, this.tamanho) - 1;
    this.focarCaixa(ultimoPreenchido < this.tamanho - 1 ? ultimoPreenchido + 1 : this.tamanho - 1);
  }

  private publicar(): void {
    const valor = this.caracteres.join('');
    this.aoMudarValor(valor);

    if (valor.length === this.tamanho) {
      this.completo.emit(valor);
    }
  }

  private normalizar(valor: string): string {
    return (valor || '').toUpperCase().replace(CodigoAcessoComponent.PERMITIDOS, '');
  }

  /**
   * O valor de cada caixa é escrito na mão, sem binding.
   *
   * A normalização muda o que o navegador já pôs no campo (minúscula vira maiúscula, caractere
   * inválido some). Com [value], quando o valor normalizado é igual ao anterior o Angular não
   * reescreve o DOM, e a caixa fica mostrando o que foi digitado em vez do que foi aceito.
   */
  private sincronizarCaixas(): void {
    this.caixas?.forEach((caixa, indice) => {
      const valor = this.caracteres[indice] ?? '';
      if (caixa.nativeElement.value !== valor) {
        caixa.nativeElement.value = valor;
      }
    });
  }

  private focarCaixa(indice: number): void {
    if (indice < 0 || indice >= this.tamanho) {
      return;
    }

    const caixa = this.caixas?.get(indice);
    caixa?.nativeElement.focus();
    caixa?.nativeElement.select();
  }
}
